import { useEffect } from 'react';

type FaviconOptions = {
  href: string;
  type?: string;
};

export const setFavicon = ({ href, type }: FaviconOptions) => {
  const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));

  if (!links.length) {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = href;
    if (type) link.type = type;
    document.head.appendChild(link);
    return;
  }

  for (const link of links) {
    link.href = href;
    if (type) link.type = type;
  }
};

let loadingTimer: number | null = null;

const startLoadingFavicon = () => {
  if (loadingTimer) return;

  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  let frame = 0;

  loadingTimer = window.setInterval(() => {
    frame++;

    ctx.clearRect(0, 0, size, size);

    const dots = 12;
    const cx = size / 2;
    const cy = size / 2;
    const radius = 10;

    for (let i = 0; i < dots; i++) {
      const t = (i + frame) % dots;
      const alpha = 0.2 + (t / (dots - 1)) * 0.8;

      const angle = (i / dots) * Math.PI * 2;
      const x = cx + Math.cos(angle) * radius;
      const y = cy + Math.sin(angle) * radius;

      ctx.beginPath();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#4F46E5';
      ctx.arc(x, y, 2.2, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalAlpha = 1;

    setFavicon({ href: canvas.toDataURL('image/png') });
  }, 180); // low CPU, smooth enough
};

const stopLoadingFavicon = (finalHref = '/favicon.ico') => {
  if (loadingTimer) {
    clearInterval(loadingTimer);
    loadingTimer = null;
  }

  setFavicon({ href: finalHref });
};

export function useStatusFavicon(status: 'idle' | 'loading' | 'success' | 'error') {
  useEffect(() => {
    switch (status) {
      case 'loading':
        startLoadingFavicon();
        break;

      case 'success':
        stopLoadingFavicon('/favicons/success.svg');
        break;

      case 'error':
        stopLoadingFavicon('/favicons/error.svg');
        break;

      case 'idle':
      default:
        stopLoadingFavicon('/favicons/favicon.ico');
        break;
    }
  }, [status]);

  useEffect(() => {
    return () => stopLoadingFavicon('/favicons/favicon.ico');
  }, []);
}
